import type { Engine } from '@/core/Engine';
import { GameStateManager, GamePhase, GameStateEvents } from './GameStateManager';
import { RoundManager } from './RoundManager';
import { CombatSystem, CombatSpeed, CombatEvents, UnitAttackedEvent } from '@/combat/CombatSystem';
import { PlacementSystem, PlacementEvents } from '@/placement/PlacementSystem';
import { BenchManager } from '@/placement/BenchManager';
import { MergeSystem, MergeEvents } from '@/merge/MergeSystem';
import { TraitSystem } from '@/traits/TraitSystem';
import { CurrencyManager } from '@/economy/CurrencyManager';
import { ShopSystem } from '@/shop/ShopSystem';
import { UnitPool } from '@/shop/UnitPool';
import { RulerSystem } from '@/rulers/RulerSystem';
import { UnitFactory } from '@/units/UnitFactory';
import { UnitRenderer } from '@/units/UnitRenderer';
import { PlayerSide } from '@/types/hex.types';
import { getMaxUnitsForRound } from '@/utils/constants';
import type { Entity } from '@/ecs/Entity';
import { Position } from '@/ecs/Component';
import { UnitMesh } from '@/ecs/components/UnitComponents';
import type { AxialCoord } from '@/types/hex.types';
import { System } from '@/ecs/System';
import { NetworkManager, NetworkEvents } from '@/network/NetworkManager';
import { setCombatSeed, clearCombatRNG } from '@/network/SeededRNG';
import type { UnitPlacement } from '@/network/messages';

/**
 * Main game controller that coordinates all game systems
 */
export class GameController extends System {
  public readonly priority = 0; // Run first

  // Engine reference
  private engine: Engine;

  // Core systems
  public readonly gameState: GameStateManager;
  public readonly roundManager: RoundManager;
  public readonly combatSystem: CombatSystem;

  // Unit systems
  public readonly unitFactory: UnitFactory;
  public readonly unitRenderer: UnitRenderer;
  public readonly placementSystem: PlacementSystem;
  public readonly benchManager: BenchManager;
  public readonly mergeSystem: MergeSystem;

  // Trait and economy systems
  public readonly traitSystem: TraitSystem;
  public readonly currencyManager: CurrencyManager;
  public readonly unitPool: UnitPool;
  public readonly shopSystem: ShopSystem;
  public readonly rulerSystem: RulerSystem;

  // Network for multiplayer
  public readonly networkManager: NetworkManager;
  private isMultiplayer = false;
  private localPlayerId: 1 | 2 = 1;
  private opponentName: string = 'AI';

  constructor(engine: Engine) {
    super();
    this.engine = engine;

    const { world, eventBus, hexGrid, hexRenderer } = engine;

    // Initialize unit systems
    this.unitRenderer = new UnitRenderer(engine.renderer.scene);
    // Set parent group to hex grid group so units inherit grid centering
    this.unitRenderer.setParentGroup(hexRenderer.getGroup());
    this.unitFactory = new UnitFactory(world, this.unitRenderer, hexGrid);

    // Initialize economy
    this.currencyManager = new CurrencyManager(eventBus);

    // Initialize placement and bench
    this.benchManager = new BenchManager(world, eventBus, this.unitFactory);
    this.placementSystem = new PlacementSystem(
      world,
      eventBus,
      hexGrid,
      hexRenderer,
      this.unitFactory,
      this.unitRenderer
    );

    // Initialize merge system
    this.mergeSystem = new MergeSystem(
      world,
      eventBus,
      this.unitFactory,
      this.unitRenderer,
      this.benchManager
    );

    // Initialize traits
    this.traitSystem = new TraitSystem(world, eventBus);

    // Initialize unit pool (shared between players)
    this.unitPool = new UnitPool(eventBus);

    // Initialize shop (with unit pool)
    this.shopSystem = new ShopSystem(
      world,
      eventBus,
      this.unitFactory,
      this.benchManager,
      this.currencyManager,
      this.unitPool
    );

    // Initialize rulers
    this.rulerSystem = new RulerSystem(eventBus, this.currencyManager);

    // Initialize network manager
    this.networkManager = new NetworkManager(eventBus);

    // Initialize combat
    this.combatSystem = new CombatSystem(hexGrid, this.unitRenderer, eventBus);

    // Initialize game state and round manager
    this.gameState = new GameStateManager(eventBus);
    this.roundManager = new RoundManager(
      world,
      eventBus,
      hexGrid,
      this.gameState,
      this.combatSystem
    );

    // Connect round manager to unit systems for position restoration
    this.roundManager.setUnitSystems(this.unitFactory, this.unitRenderer);

    // Connect placement system to shop for direct board purchases
    this.placementSystem.setShopPurchaseCallback((slotIndex: number) => {
      // Use local player ID (supports multiplayer)
      const playerId = this.isMultiplayer ? this.localPlayerId : 1;
      const result = this.shopSystem.purchaseSlot(playerId, slotIndex);
      return result !== null;
    });

    // Connect placement system to merge system for merge-on-place
    this.placementSystem.setMergeSystem(this.mergeSystem);

    // Connect placement system to network manager for multiplayer sync
    this.placementSystem.setNetworkManager(this.networkManager);

    this.setupEventListeners();
    this.setupNetworkListeners();
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    const { eventBus } = this.engine;

    // Phase change handling
    eventBus.on<{ previousPhase: GamePhase; newPhase: GamePhase }>(
      GameStateEvents.PHASE_CHANGED,
      (data) => {
        this.onPhaseChanged(data.newPhase);
      }
    );

    // Round events
    eventBus.on(GameStateEvents.ROUND_STARTED, () => {
      this.onRoundStarted();
    });

    eventBus.on(GameStateEvents.ROUND_ENDED, () => {
      this.onRoundEnded();
    });

    // Placement events - recalculate traits when units are placed
    eventBus.on(PlacementEvents.UNIT_PLACED, () => {
      this.recalculateTraits();
    });

    eventBus.on(PlacementEvents.UNIT_SWAPPED, () => {
      this.recalculateTraits();
    });

    // Merge events - recalculate traits after merge
    eventBus.on(MergeEvents.MERGE_COMPLETED, () => {
      this.recalculateTraits();
    });

    // Combat events - flash hex and show attack indicator on damage
    eventBus.on<UnitAttackedEvent>(CombatEvents.UNIT_ATTACKED, (data) => {
      const attacker = this.engine.world.getEntity(data.attackerId);
      const target = this.engine.world.getEntity(data.targetId);

      if (target) {
        // Flash the hex where damage was taken
        const position = target.getComponent(Position);
        if (position) {
          this.engine.hexRenderer.flashDamage({ q: position.q, r: position.r });
        }

        // Show attack indicator arrow from attacker to target
        if (attacker) {
          const attackerMesh = attacker.getComponent(UnitMesh);
          const targetMesh = target.getComponent(UnitMesh);
          if (attackerMesh && targetMesh) {
            this.unitRenderer.showAttackIndicator(attackerMesh.meshId, targetMesh.meshId);
          }
        }

        // Show floating damage number
        const targetMesh = target.getComponent(UnitMesh);
        if (targetMesh) {
          this.unitRenderer.showDamageNumber(targetMesh.meshId, data.damage, data.isCritical);
        }
      }
    });
  }

  /**
   * Setup network event listeners for multiplayer
   */
  private setupNetworkListeners(): void {
    const { eventBus } = this.engine;

    // Match found - store player ID and set multiplayer mode
    eventBus.on<{ playerId: 1 | 2; roomId: string; opponentName?: string }>(
      NetworkEvents.MATCH_FOUND,
      (data) => {
        this.isMultiplayer = true;
        this.localPlayerId = data.playerId;
        this.opponentName = data.opponentName || 'Opponent';
        this.roundManager.setMultiplayerMode(true);
        console.log(`[Game] Match found! Playing as Player ${data.playerId} vs ${this.opponentName}`);

        // Enable mirroring for Player 2 so local player always appears at bottom
        const shouldMirror = data.playerId === 2;
        this.engine.hexRenderer.setMirroring(data.playerId, shouldMirror);
        this.unitRenderer.setMirroring(data.playerId, shouldMirror);
        console.log(`[Game] Mirroring ${shouldMirror ? 'enabled' : 'disabled'} for Player ${data.playerId}`);
      }
    );

    // Game start - initialize multiplayer game
    eventBus.on<{ poolState: { available: Record<string, number> }; startingGold: number }>(
      NetworkEvents.GAME_START,
      (data) => {
        if (!this.isMultiplayer) return;
        // Pool state is managed by server in multiplayer
        this.currencyManager.setGold(this.localPlayerId, data.startingGold);
        console.log('[Game] Multiplayer game started');
      }
    );

    // Phase change from server
    eventBus.on<{ phase: string; roundNumber: number; combatSeed?: number; prepTime?: number; opponentBoard?: UnitPlacement[] }>(
      NetworkEvents.PHASE_CHANGE,
      (data) => {
        if (!this.isMultiplayer) return;
        this.handleNetworkPhaseChange(data.phase, data.roundNumber, data.combatSeed, data.prepTime, data.opponentBoard);
      }
    );

    // Shop refresh from server
    eventBus.on<{ slots: Array<{ unitType: string; tier: number; cost: number }>; gold: number }>(
      NetworkEvents.SHOP_REFRESH,
      (data) => {
        if (!this.isMultiplayer) return;
        // Update gold from server
        this.currencyManager.setGold(this.localPlayerId, data.gold);
        // Sync shop state to local ShopSystem (this emits SHOP_REFRESHED for UI)
        this.shopSystem.syncFromServer(this.localPlayerId, data.slots);
        console.log('[Game] Shop refreshed from server:', data.slots);
      }
    );

    // Opponent board received at prep phase end
    eventBus.on<{ units: UnitPlacement[] }>(
      NetworkEvents.OPPONENT_BOARD,
      (data) => {
        if (!this.isMultiplayer) return;
        this.spawnOpponentUnits(data.units);
      }
    );

    // Round result
    eventBus.on<{ winnerId: 0 | 1 | 2; scores: { playerId: number; score: number }[] }>(
      NetworkEvents.ROUND_RESULT,
      (data) => {
        if (!this.isMultiplayer) return;

        // Force-stop combat if still running (server authority)
        if (this.combatSystem.active) {
          console.log('[Game] Server declared round over - force stopping local combat');
          this.combatSystem.forceStop();
        }

        // Update scores
        for (const score of data.scores) {
          if (score.playerId === 1) {
            this.gameState.player1Score = score.score;
          } else if (score.playerId === 2) {
            this.gameState.player2Score = score.score;
          }
        }
        // Clear combat RNG for next round
        clearCombatRNG();
      }
    );

    // Combat ended - report result to server
    eventBus.on(CombatEvents.COMBAT_ENDED, () => {
      if (!this.isMultiplayer) return;
      // Determine winner and report to server
      const winner = this.combatSystem.getWinner() as 0 | 1 | 2;
      console.log(`[Game] Combat ended, reporting winner: ${winner}`);
      this.networkManager.reportBattleResult(winner);
    });
  }

  /**
   * Handle phase change from network
   */
  private handleNetworkPhaseChange(phase: string, roundNumber: number, combatSeed?: number, prepTime?: number, opponentBoard?: UnitPlacement[]): void {
    // Map server phase to local phase
    const phaseMap: Record<string, GamePhase> = {
      preparation: GamePhase.Preparation,
      battle: GamePhase.Battle,
      roundEnd: GamePhase.RoundEnd,
      matchEnd: GamePhase.MatchEnd,
    };

    const localPhase = phaseMap[phase];
    if (!localPhase) return;

    console.log(`[Game] Network phase change: ${phase}, round ${roundNumber}`);

    // Set combat seed before battle starts
    if (phase === 'battle' && combatSeed !== undefined) {
      setCombatSeed(combatSeed);
      console.log(`[Game] Combat seed set: ${combatSeed}`);
    }

    // Spawn opponent units BEFORE changing phase to battle
    // This ensures they are available when combat starts
    if (phase === 'battle' && opponentBoard) {
      console.log(`[Game] Spawning ${opponentBoard.length} opponent units before battle`);
      this.spawnOpponentUnits(opponentBoard);
    }

    // Update game state
    this.gameState.currentRound = roundNumber;

    // Set battle timer for multiplayer (prevents immediate timeout)
    if (phase === 'battle') {
      // Use a long battle timer - server controls when battle ends
      this.gameState.setPhaseTimer(0, 300); // 5 minutes max
    }

    // For preparation phase, set timer and emit round started
    if (phase === 'preparation') {
      // Set the timer for prep phase
      this.gameState.setPhaseTimer(0, prepTime ?? 30);

      // Emit round started event (this triggers trait recalculation, etc.)
      this.engine.eventBus.emit(GameStateEvents.ROUND_STARTED, {
        roundNumber: roundNumber,
      });
    }

    // Set the phase (this emits PHASE_CHANGED)
    this.gameState.setPhase(localPhase);
  }

  /**
   * Spawn opponent units received from server
   */
  private spawnOpponentUnits(units: UnitPlacement[]): void {
    const opponentId = this.localPlayerId === 1 ? 2 : 1;

    console.log(`[Game] Received opponent board with ${units.length} units for player ${opponentId}:`, units);

    // Clear existing opponent units first
    this.clearPlayerUnits(opponentId);

    // Spawn new units at their actual coordinates (no mirroring needed)
    // Player 1's units are in rows 5-7, Player 2's units are in rows 0-2
    // Each player sees opponent units in the opponent's territory
    for (const unit of units) {
      const created = this.unitFactory.createUnit(unit.unitType, opponentId, unit.coord, unit.starLevel);
      if (created) {
        console.log(`[Game] Created opponent unit: ${unit.unitType} at (${unit.coord.q}, ${unit.coord.r})`);
      } else {
        console.error(`[Game] Failed to create opponent unit: ${unit.unitType} at (${unit.coord.q}, ${unit.coord.r})`);
      }
    }

    console.log(`[Game] Spawned ${units.length} opponent units`);
  }

  /**
   * Clear all units for a player
   */
  private clearPlayerUnits(playerId: number): void {
    const entities = this.engine.world.getEntitiesByOwner(playerId);
    for (const entity of entities) {
      this.unitFactory.removeUnit(entity);
    }
  }


  /**
   * Handle phase change
   */
  private onPhaseChanged(newPhase: GamePhase): void {
    // In multiplayer, server controls phases
    if (this.isMultiplayer) {
      switch (newPhase) {
        case GamePhase.Preparation:
          // Enable placement for local player
          this.placementSystem.enable(this.localPlayerId, this.gameState.currentRound);
          break;
        case GamePhase.Battle:
          this.placementSystem.disable();
          // Notify server that we're ready (in case of early battle)
          this.networkManager.ready();
          break;
      }
      return;
    }

    // Single-player logic
    switch (newPhase) {
      case GamePhase.Preparation:
        // Enable placement for player 1 with current round limit
        this.placementSystem.enable(1, this.gameState.currentRound);
        // On round 1, spawn one random starter unit for Player 1
        if (this.gameState.currentRound === 1) {
          this.spawnStarterUnitForPlayer1();
        }
        // Spawn random units for Player 2
        this.spawnRandomUnitsForPlayer2();
        break;
      case GamePhase.Battle:
        this.placementSystem.disable();
        break;
      case GamePhase.RoundEnd:
        // Could show results UI
        break;
    }
  }

  /**
   * Spawn one random starter unit for Player 1 (only on round 1)
   */
  private spawnStarterUnitForPlayer1(): void {
    // Get empty P1 tiles
    const emptyTiles = this.engine.hexGrid.getEmptyPlayerTiles(PlayerSide.Player1);
    if (emptyTiles.length === 0) return;

    // Get available tier 1 units only for starter
    const availableUnits = this.unitPool.getAvailableUnitsOfTier(1);
    if (availableUnits.length === 0) return;

    // Pick a random tile and unit
    const randomTile = emptyTiles[Math.floor(Math.random() * emptyTiles.length)];
    const randomUnit = availableUnits[Math.floor(Math.random() * availableUnits.length)];

    // Take from pool and spawn
    if (this.unitPool.take(randomUnit.unitType)) {
      this.unitFactory.createUnit(randomUnit.unitType, 1, randomTile.coord, 1);
      console.log(`Round 1: Spawned starter unit ${randomUnit.unitType} for P1`);
    }
  }

  /**
   * Spawn random units for Player 2 (AI)
   */
  private spawnRandomUnitsForPlayer2(): void {
    const currentRound = this.gameState.currentRound;
    const maxUnits = getMaxUnitsForRound(currentRound);

    // Check current P2 unit count
    const p2UnitCount = this.placementSystem.getUnitCount(2);
    const unitsToSpawn = maxUnits - p2UnitCount;

    if (unitsToSpawn <= 0) return;

    // Get empty P2 tiles
    const emptyTiles = this.engine.hexGrid.getEmptyPlayerTiles(PlayerSide.Player2);
    if (emptyTiles.length === 0) return;

    // Get available unit types from pool (tier 1-3 for AI)
    const availableUnits = [
      ...this.unitPool.getAvailableUnitsOfTier(1),
      ...this.unitPool.getAvailableUnitsOfTier(2),
      ...this.unitPool.getAvailableUnitsOfTier(3),
    ];
    if (availableUnits.length === 0) return;

    // Spawn units up to the round limit
    const numUnits = Math.min(unitsToSpawn, emptyTiles.length);

    // Shuffle tiles to get random positions
    const shuffledTiles = [...emptyTiles].sort(() => Math.random() - 0.5);

    let spawned = 0;
    for (let i = 0; i < numUnits && availableUnits.length > 0; i++) {
      // Pick a random available unit type
      const randomIndex = Math.floor(Math.random() * availableUnits.length);
      const unit = availableUnits[randomIndex];
      const tile = shuffledTiles[i];

      // Try to take from pool
      if (this.unitPool.take(unit.unitType)) {
        this.unitFactory.createUnit(unit.unitType, 2, tile.coord, 1);
        spawned++;

        // If this unit is no longer available, remove from our list
        if (!this.unitPool.isAvailable(unit.unitType)) {
          availableUnits.splice(randomIndex, 1);
        }
      }
    }

    console.log(`Round ${currentRound}: Spawned ${spawned} units for P2 (max: ${maxUnits})`);
  }

  /**
   * Handle round start
   */
  private onRoundStarted(): void {
    // Refresh shops
    this.shopSystem.initializeRound();

    // Apply ruler round bonuses
    this.rulerSystem.applyRoundStartBonuses();

    // Recalculate traits
    this.recalculateTraits();
  }

  /**
   * Handle round end
   */
  private onRoundEnded(): void {
    // Process economy
    const winner = this.combatSystem.getWinner();
    this.currencyManager.processRoundResult(winner);
  }

  /**
   * Recalculate traits for both players
   */
  private recalculateTraits(): void {
    this.traitSystem.calculateTraits(1);
    this.traitSystem.calculateTraits(2);
  }

  /**
   * Initialize game systems (called when added to world)
   */
  override init(world: import('@/ecs/World').World): void {
    super.init(world);
    // Add combat system to world
    world.addSystem(this.combatSystem);
  }

  /**
   * Update game systems each frame
   */
  override update(deltaTime: number): void {
    // Update round manager (handles timers)
    this.roundManager.update(deltaTime);

    // Update unit renderer (animations)
    // deltaTime is already in seconds from the game loop
    this.unitRenderer.update(deltaTime);

    // Update outline objects for post-processing
    this.engine.postProcessor.setOutlineObjects(this.unitRenderer.getAllMeshes());

    // Clean up destroyed bench units
    this.benchManager.cleanupDestroyedUnits();
  }

  // === Public API for game actions ===

  /**
   * Start a new game
   */
  startGame(): void {
    // Reset the unit pool for the new match
    this.unitPool.reset();
    this.gameState.startMatch();
  }

  /**
   * Select ruler and proceed
   */
  selectRuler(playerId: number, rulerId: string): boolean {
    return this.rulerSystem.selectRuler(playerId, rulerId);
  }

  /**
   * Skip ruler selection (for testing)
   */
  skipRulerSelection(): void {
    this.gameState.skipRulerSelect();
  }

  /**
   * Start battle immediately
   */
  startBattle(): void {
    this.roundManager.startBattleNow();
  }

  /**
   * Proceed after round end
   */
  proceedToNextRound(): void {
    this.roundManager.proceedToNextRound();
  }

  /**
   * Set combat speed
   */
  setCombatSpeed(speed: CombatSpeed): void {
    this.combatSystem.setCombatSpeed(speed);
  }

  /**
   * Spawn a unit for testing
   */
  spawnTestUnit(
    unitType: string,
    playerId: number,
    coord: AxialCoord,
    starLevel: number = 1
  ): Entity | null {
    return this.unitFactory.createUnit(unitType, playerId, coord, starLevel);
  }

  /**
   * Buy unit from shop
   */
  buyUnit(playerId: number, slotIndex: number): Entity | null {
    return this.shopSystem.buyUnit(playerId, slotIndex);
  }

  /**
   * Sell a unit
   */
  sellUnit(entity: Entity, playerId: number): number {
    return this.shopSystem.sellUnit(entity, playerId);
  }

  /**
   * Reroll shop
   */
  rerollShop(playerId: number): boolean {
    return this.shopSystem.refreshShop(playerId);
  }

  /**
   * Toggle shop lock
   */
  toggleShopLock(playerId: number): boolean {
    return this.shopSystem.toggleLock(playerId);
  }

  /**
   * Get current game info
   */
  getGameInfo(): {
    phase: GamePhase;
    round: number;
    scores: { player1: number; player2: number };
    timeRemaining: number;
    player1Gold: number;
    player2Gold: number;
  } {
    const roundInfo = this.roundManager.getInfo();
    return {
      ...roundInfo,
      player1Gold: this.currencyManager.getGold(1),
      player2Gold: this.currencyManager.getGold(2),
    };
  }

  /**
   * Get shop state
   */
  getShopState(playerId: number) {
    return {
      slots: this.shopSystem.getShopState(playerId),
      locked: this.shopSystem.isLocked(playerId),
      gold: this.currencyManager.getGold(playerId),
    };
  }

  /**
   * Get active traits for a player
   */
  getActiveTraits(playerId: number) {
    return this.traitSystem.getActiveTraits(playerId);
  }

  /**
   * Get unit count
   */
  getUnitCount(playerId: number): number {
    return this.placementSystem.getUnitCount(playerId);
  }

  /**
   * Get max units allowed for the current round
   */
  getMaxUnitsForCurrentRound(): number {
    return getMaxUnitsForRound(this.gameState.currentRound);
  }

  /**
   * Get remaining unit slots for a player in the current round
   */
  getRemainingUnitSlots(playerId: number): number {
    const maxUnits = this.getMaxUnitsForCurrentRound();
    const currentUnits = this.getUnitCount(playerId);
    return Math.max(0, maxUnits - currentUnits);
  }

  // === Multiplayer API ===

  /**
   * Check if in multiplayer mode
   */
  isMultiplayerMode(): boolean {
    return this.isMultiplayer;
  }

  /**
   * Get local player ID in multiplayer
   */
  getLocalPlayerId(): 1 | 2 {
    return this.localPlayerId;
  }

  /**
   * Get opponent name (for display)
   */
  getOpponentName(): string {
    return this.opponentName;
  }

  /**
   * Connect to multiplayer server
   */
  connectToServer(): void {
    this.networkManager.connect();
  }

  /**
   * Join matchmaking queue
   * @param playerName Optional player name to send to server
   */
  joinMatchmaking(playerName?: string): void {
    this.networkManager.joinQueue(playerName);
  }

  /**
   * Leave matchmaking queue
   */
  leaveMatchmaking(): void {
    this.networkManager.leaveQueue();
  }

  /**
   * Disconnect from server
   */
  disconnectFromServer(): void {
    this.networkManager.disconnect();
    this.isMultiplayer = false;
  }

  /**
   * Return to main menu (ends current game)
   */
  returnToMainMenu(): void {
    // Disconnect from multiplayer if connected
    if (this.isMultiplayer) {
      this.networkManager.disconnect();
      this.isMultiplayer = false;
      this.opponentName = 'AI';
    }

    // Reset multiplayer mode on round manager
    this.roundManager.setMultiplayerMode(false);

    // Reset mirroring state
    this.engine.hexRenderer.setMirroring(null, false);
    this.unitRenderer.setMirroring(null, false);

    // Clear all units from the board
    this.clearPlayerUnits(1);
    this.clearPlayerUnits(2);

    // Clear bench units
    this.benchManager.reset();

    // Reset unit pool
    this.unitPool.reset();

    // Reset currency
    this.currencyManager.reset();

    // Return to main menu
    this.gameState.returnToMainMenu();
  }

  /**
   * Clean up
   */
  override dispose(): void {
    this.placementSystem.dispose();
    this.unitRenderer.dispose();
    this.networkManager.disconnect();
    super.dispose();
  }
}

/**
 * Create and initialize game controller
 */
export function createGameController(engine: Engine): GameController {
  const controller = new GameController(engine);
  engine.world.addSystem(controller);
  return controller;
}
